import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import api, { formatINR } from '@/lib/api';
import { toast } from 'sonner';
import { Storefront, Package, CurrencyInr, Clock, QrCode, X } from '@phosphor-icons/react';

export default function SellerDashboard() {
  const { seller, refreshSeller } = useOutletContext();
  const [stats, setStats] = useState({ orders_today: 0, orders_month: 0, revenue_month: 0, commission_month: 0, products: 0 });
  const [recent, setRecent] = useState([]);
  const [savingToggle, setSavingToggle] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const storeUrl = seller?.slug ? `${window.location.origin}/marketplace/store/${seller.slug}` : '';
  const qrSrc = storeUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=10&data=${encodeURIComponent(storeUrl)}` : '';
  const qrDownloadSrc = storeUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=1024x1024&margin=20&data=${encodeURIComponent(storeUrl)}&download=1` : '';

  useEffect(() => {
    if (!seller || seller.status !== 'approved') return;
    (async () => {
      try {
        const [ordersRes, productsRes] = await Promise.all([
          api.get('/sellers/me/orders'),
          api.get('/sellers/me/products'),
        ]);
        const orders = ordersRes.data || [];
        const products = productsRes.data || [];
        const today = new Date().toISOString().slice(0, 10);
        const month = today.slice(0, 7);
        const ordersToday = orders.filter((o) => (o.created_at || '').slice(0, 10) === today);
        const ordersMonth = orders.filter((o) => (o.created_at || '').slice(0, 7) === month);
        const revenueMonth = ordersMonth.filter((o) => o.status === 'delivered').reduce((s, o) => s + Number(o.subtotal || 0), 0);
        const commissionMonth = ordersMonth.filter((o) => o.status === 'delivered').reduce((s, o) => s + Number(o.commission_amount || 0), 0);
        setStats({
          orders_today: ordersToday.length,
          orders_month: ordersMonth.length,
          revenue_month: revenueMonth,
          commission_month: commissionMonth,
          products: products.length,
        });
        setRecent(orders.slice(0, 6));
      } catch (e) { /* ignore */ }
    })();
  }, [seller]);

  const toggleOpen = async () => {
    setSavingToggle(true);
    try {
      await api.patch('/sellers/me', { is_open: !seller.is_open });
      await refreshSeller();
      toast.success(!seller.is_open ? 'Store is now OPEN' : 'Store is now CLOSED');
    } catch (e) { toast.error('Could not update'); } finally { setSavingToggle(false); }
  };

  return (
    <div className="space-y-6" data-testid="seller-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-stone-900">Dashboard</h1>
          <p className="text-sm text-stone-500">Overview of your store performance.</p>
        </div>
        {seller?.status === 'approved' && (
          <button
            data-testid="seller-toggle-open"
            onClick={toggleOpen}
            disabled={savingToggle}
            className={`h-10 px-5 rounded-full text-sm font-semibold border ${seller.is_open ? 'bg-olive-100 border-olive-300 text-olive-800 hover:bg-olive-200' : 'bg-stone-100 border-stone-300 text-stone-700 hover:bg-stone-200'}`}
          >
            {seller.is_open ? '● Store OPEN · click to close' : '○ Store CLOSED · click to open'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Orders today" value={stats.orders_today} icon={<Clock size={20}/>} testId="stat-orders-today"/>
        <Stat label="Orders this month" value={stats.orders_month} icon={<Package size={20}/>} testId="stat-orders-month"/>
        <Stat label="Revenue (delivered)" value={formatINR(stats.revenue_month)} icon={<CurrencyInr size={20}/>} testId="stat-revenue"/>
        <Stat label="Commission paid" value={formatINR(stats.commission_month)} icon={<CurrencyInr size={20}/>} testId="stat-commission"/>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg text-stone-900">Recent orders</h2>
          <a href="/seller/orders" className="text-xs text-olive-800 hover:underline">View all →</a>
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-stone-500 py-6 text-center">No orders yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stone-500 text-xs uppercase">
                <th className="py-2">Order</th><th>Customer</th><th>Total</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((o) => (
                <tr key={o.id} className="border-t border-stone-100">
                  <td className="py-2 font-mono text-xs">{o.order_no}</td>
                  <td>{o.customer_name}</td>
                  <td>{formatINR(o.total)}</td>
                  <td><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 uppercase">{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {seller?.status === 'approved' && (
        <div className="bg-gradient-to-br from-olive-50 to-cream-100 border border-olive-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <Storefront size={32} className="text-olive-700"/>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-stone-900">Your store page</p>
            <p className="text-sm text-stone-600 truncate">{storeUrl}</p>
          </div>
          <button
            data-testid="seller-show-qr-btn"
            onClick={() => setShowQR(true)}
            className="h-9 px-4 rounded-full bg-white border border-olive-300 text-olive-800 text-xs font-semibold hover:bg-olive-50 inline-flex items-center gap-1.5"
          >
            <QrCode size={14} weight="bold"/> Show QR
          </button>
          <button
            onClick={() => { navigator.clipboard.writeText(storeUrl); toast.success('Link copied'); }}
            className="h-9 px-4 rounded-full bg-olive-700 text-white text-xs font-semibold hover:bg-olive-800"
          >
            Copy store link
          </button>
        </div>
      )}

      {showQR && seller?.status === 'approved' && (
        <div
          data-testid="seller-qr-modal"
          className="fixed inset-0 z-[80] bg-stone-900/70 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setShowQR(false)}
        >
          <div
            className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="seller-qr-close"
              onClick={() => setShowQR(false)}
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 grid place-items-center"
            >
              <X size={18} weight="bold"/>
            </button>
            <div className="text-center space-y-3">
              <h3 className="font-heading text-xl text-stone-900">{seller?.shop_name || 'My Store'}</h3>
              <p className="text-xs text-stone-500">Scan to visit this store</p>
              <div className="bg-cream-50 border border-stone-200 rounded-2xl p-4 grid place-items-center">
                <img
                  data-testid="seller-qr-image"
                  src={qrSrc}
                  alt="Store QR code"
                  width={320}
                  height={320}
                  className="w-full max-w-[280px] h-auto"
                />
              </div>
              <p className="text-[11px] text-stone-500 break-all">{storeUrl}</p>
              <div className="flex gap-2 pt-2">
                <a
                  data-testid="seller-qr-download"
                  href={qrDownloadSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 h-10 rounded-full bg-olive-700 text-white text-sm font-semibold hover:bg-olive-800 inline-flex items-center justify-center"
                >
                  Download HD
                </a>
                <button
                  onClick={() => window.print()}
                  className="flex-1 h-10 rounded-full bg-white border border-stone-300 text-stone-700 text-sm font-semibold hover:bg-stone-50"
                >
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-2xl border border-stone-200 p-4">
      <div className="flex items-center gap-2 text-stone-500">{icon}<span className="text-xs uppercase tracking-wider">{label}</span></div>
      <p className="font-heading text-2xl text-stone-900 mt-1">{value}</p>
    </div>
  );
}
